import { useState, useEffect } from 'react';
import { Link2, Star, Trash2, Plus, Edit2, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';

interface WebDomain {
  id: number;
  contact_id: string;
  website_domain: string;
  is_primary: boolean;
  created_at: string;
  label?: string;
}

interface ContactWebDomainsProps {
  contactId: string;
  webDomains: WebDomain[];
  onUpdate: () => void;
}

export function ContactWebDomains({ contactId, webDomains: webDomainsProp, onUpdate }: ContactWebDomainsProps) {
  const [localDomains, setLocalDomains] = useState<WebDomain[]>(webDomainsProp);
  const [newDomain, setNewDomain] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedDomain, setEditedDomain] = useState('');
  const [editedLabel, setEditedLabel] = useState('');
  const { toast } = useToast();

  // Sync local state when parent prop changes (after refetch)
  useEffect(() => {
    setLocalDomains(webDomainsProp);

    // Auto-set primary if there's exactly one domain and it's not primary
    if (webDomainsProp.length === 1 && !webDomainsProp[0].is_primary) {
      supabase
        .from('website_domains')
        .update({ is_primary: true })
        .eq('id', webDomainsProp[0].id)
        .then(() => onUpdate());
    }
    // If there are multiple and none is primary, set the first one
    if (webDomainsProp.length > 1 && !webDomainsProp.some(d => d.is_primary)) {
      supabase
        .from('website_domains')
        .update({ is_primary: true })
        .eq('id', webDomainsProp[0].id)
        .then(() => onUpdate());
    }
  }, [webDomainsProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatUrl = (domain: string) => {
    if (!domain) return '';
    return domain.startsWith('http') ? domain : `https://${domain}`;
  };

  const addWebDomain = async () => {
    try {
      if (!newDomain.trim()) {
        toast({
          title: "Validation Error",
          description: "Please enter a domain",
          variant: "destructive",
        });
        return;
      }

      const domainValue = newDomain.trim().toLowerCase();
      
      // Simple domain validation
      const domainPattern = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+)(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;
      if (!domainPattern.test(domainValue)) {
        toast({
          title: "Validation Error",
          description: "Please enter a valid domain",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.from('website_domains').insert({
        contact_id: contactId,
        website_domain: domainValue,
        is_primary: localDomains.length === 0, // First domain is primary
        label: newLabel.trim() || null
      });

      if (error) throw error;

      setNewDomain('');
      setNewLabel('');
      setIsAdding(false);
      onUpdate();
      
      toast({
        description: "Website domain added successfully",
      });
    } catch (error) {
      console.error('Error adding web domain:', error);
      captureError(error as Error, { feature: 'contacts-contact-web-domains-add-web-domain' });
      toast({
        title: "Error",
        description: "Failed to add website domain",
        variant: "destructive",
      });
    }
  };

  const deleteWebDomain = async (id: number) => {
    try {
      const { error } = await supabase
        .from('website_domains')
        .delete()
        .eq('id', id);

      if (error) throw error;

      onUpdate();
      
      toast({
        description: "Website domain removed successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-web-domains-delete-web-domain' });
      toast({
        title: "Error",
        description: "Failed to remove website domain",
        variant: "destructive",
      });
    }
  };

  const setPrimary = async (id: number) => {
    try {
      // First, set all domains to non-primary
      const { error: resetError } = await supabase
        .from('website_domains')
        .update({ is_primary: false })
        .eq('contact_id', contactId);

      if (resetError) throw resetError;

      // Then set the selected domain as primary
      const { error: updateError } = await supabase
        .from('website_domains')
        .update({ is_primary: true })
        .eq('id', id);

      if (updateError) throw updateError;

      onUpdate();
      
      toast({
        description: "Primary website domain updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-web-domains-set-primary' });
      toast({
        title: "Error",
        description: "Failed to update primary website domain",
        variant: "destructive",
      });
    }
  };

  const updateWebDomain = async (id: number) => {
    try {
      if (!editedDomain.trim()) {
        toast({
          title: "Validation Error",
          description: "Please enter a domain",
          variant: "destructive",
        });
        return;
      }

      const domainValue = editedDomain.trim().toLowerCase();
      
      // Simple domain validation
      const domainPattern = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+)(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;
      if (!domainPattern.test(domainValue)) {
        toast({
          title: "Validation Error",
          description: "Please enter a valid domain",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('website_domains')
        .update({ 
          website_domain: domainValue,
          label: editedLabel.trim() || null
        })
        .eq('id', id);

      if (error) throw error;

      // Optimistically update local state immediately
      setLocalDomains(prev => prev.map(d =>
        d.id === id ? { ...d, website_domain: domainValue, label: editedLabel.trim() || undefined } : d
      ));

      setEditingId(null);
      setEditedDomain('');
      setEditedLabel('');
      onUpdate();

      toast({
        description: "Website domain updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-web-domains-update-web-domain' });
      toast({
        title: "Error",
        description: "Failed to update website domain",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-2">
      <h2 className="font-semibold text-sm">Web Domains</h2>
      
      {localDomains.map((domain) => (
        <div
          key={domain.id}
          className="group flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
        >
          <div className="flex gap-2">
            <Link2 className="h-3.5 w-3.5 text-muted-foreground mt-1" />
            <div>
              {editingId === domain.id ? (
                <div className="space-y-2 p-2 border rounded-lg w-full min-w-[280px]">
                  <Input
                    value={editedDomain}
                    onChange={(e) => setEditedDomain(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        updateWebDomain(domain.id);
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    placeholder="Enter domain"
                    className="h-7 text-xs"
                    autoFocus
                  />
                  <Input
                    value={editedLabel}
                    onChange={(e) => setEditedLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        updateWebDomain(domain.id);
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    placeholder="Label (optional)"
                    className="h-7 text-xs"
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={() => updateWebDomain(domain.id)}
                      size="sm"
                      className="h-7 text-xs"
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setEditingId(null);
                        setEditedDomain('');
                        setEditedLabel('');
                      }}
                      size="sm"
                      className="h-7 text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <a
                      href={formatUrl(domain.website_domain)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs hover:underline"
                    >
                      {domain.website_domain}
                    </a>
                    {domain.is_primary && (
                      <Star className="h-3.5 w-3.5 fill-current text-yellow-500" />
                    )}
                  </div>
                  {domain.label && (
                    <div className="text-xs text-muted-foreground">
                      {domain.label}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          {!editingId && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setEditingId(domain.id);
                  setEditedDomain(domain.website_domain);
                  setEditedLabel(domain.label || '');
                }}
                className="h-6 w-6"
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigator.clipboard.writeText(domain.website_domain)}
                className="h-6 w-6"
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.open(formatUrl(domain.website_domain), '_blank')}
                className="h-6 w-6"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
              {!domain.is_primary && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPrimary(domain.id)}
                  className="h-6 w-6"
                >
                  <Star className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deleteWebDomain(domain.id)}
                className="h-6 w-6 text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      ))}
      
      {isAdding ? (
        <div className="space-y-2 p-2 border rounded-lg">
          <div className="grid gap-2">
            <Input
              placeholder="Enter website domain (e.g., example.com)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newDomain.trim()) {
                  e.preventDefault();
                  addWebDomain();
                } else if (e.key === 'Escape') {
                  setIsAdding(false);
                  setNewDomain('');
                  setNewLabel('');
                }
              }}
              className="h-7 text-xs"
            />
            <Input
              placeholder="Label (Optional)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newDomain.trim()) {
                  e.preventDefault();
                  addWebDomain();
                } else if (e.key === 'Escape') {
                  setIsAdding(false);
                  setNewDomain('');
                  setNewLabel('');
                }
              }}
              className="h-7 text-xs"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              onClick={addWebDomain}
              size="sm"
              className="h-7 text-xs"
              disabled={!newDomain.trim()}
            >
              Add
            </Button>
            <Button 
              variant="ghost"
              onClick={() => {
                setIsAdding(false);
                setNewDomain('');
                setNewLabel('');
              }}
              size="sm"
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground text-xs h-7"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Web Domain
        </Button>
      )}
    </div>
  );
}